import React, { useEffect, useState } from 'react'
import { msgid, ngettext, t } from 'ttag'
import { useLocation } from 'react-router-dom'
import dayjs from '../../dayjsConfig'
import { GoalWithProgress } from '../../services/api.types'
import { currentLangString, isRtl } from '../../i18nInit'
import { convertUTCToLocalDate, getShortDateString } from '../../common/helpers/date'
import { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import makeStyles from '@material-ui/core/styles/makeStyles'

const getChallengeTabLabel = (tab?: string): string => {
  switch (tab) {
    case 'info':
      return t`Info`
    case 'me':
      return t`Me`
    case 'ranking':
      return t`Ranking`
    case 'leaderboard':
      return t`Leaderboard`
    case 'chat':
      return t`Chat`
    default:
      return ''
  }
}

export interface IChallengeRankingProps {
  goal?: GoalWithProgress
  className?: string
  noMargin?: boolean
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    textAlign: 'center',
    overflow: 'hidden',
  },
  title: {
    fontWeight: 700,
    //fontSize: '1.0625rem',
    fontSize: '17px',
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: 0,
  },
  time: {
    fontSize: '16px',
    //lineHeight: '20px',
  },
}))

export default function YumChallengeTitle(props: IChallengeRankingProps) {
  const { goal, className, noMargin } = props
  const { pathname } = useLocation()
  if (!goal) return <></>

  useEffect(() => {
    setTimeout(() => {
      document.body.classList.add('show-back-btn')
      document.body.classList.add('challenge-view')
    }, 0)
    return function cleanup() {
      document.body.classList.remove('show-back-btn')
      document.body.classList.remove('challenge-view')
    }
  }, [])

  // Override the generic route title with "{Tab} · {Challenge name} · YuMuuv"
  // while a challenge is being viewed. Layout's PageTitle resets this on next nav.
  useEffect(() => {
    if (!goal?.title) return
    const tab = pathname.split('/')[3]
    const tabLabel = getChallengeTabLabel(tab)
    document.title = tabLabel ? `${tabLabel} · ${goal.title} · YuMuuv` : `${goal.title} · YuMuuv`
  }, [goal?.title, pathname])

  const classes = useStyles()
  const { title, startDate, endDate } = goal

  const getRelativeTime = () => {
    if (convertUTCToLocalDate(new Date(endDate)).getTime() < new Date().getTime()) {
      const challengeEndDate = getShortDateString(endDate)
      return t`Ended ${challengeEndDate}`
    } else if (convertUTCToLocalDate(new Date(startDate)).getTime() > new Date().getTime()) {
      const challengeStartDate = getShortDateString(startDate)
      return t`Starts ${challengeStartDate}`
      // const daysToGo = dayjs(new Date(startDate)).diff(convertUTCToLocalDate(new Date()), 'days')
      //return daysToGo === 0 ? t`Starts tomorrow` : daysToGo === 1 ? t`1 day to go` : t`${daysToGo} days to go`
    } else {
      const daysLeft = dayjs(convertUTCToLocalDate(new Date(endDate))).diff(new Date(), 'days')
      return daysLeft === 0 ? t`Last day` : daysLeft === 1 ? t`1 day left` : t`${daysLeft} days left`
    }
  }

  const [relativeTime, setRelativeTime] = useState(getRelativeTime())

  useEffect(() => {
    let oldRelativeTime = relativeTime
    let appStateChangeListener: PluginListenerHandle | undefined
    const addListener = async () => {
      appStateChangeListener = await App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          // Check if time has changed.
          const newRelativeTime = getRelativeTime()
          if (oldRelativeTime !== newRelativeTime) {
            setRelativeTime(newRelativeTime)
            oldRelativeTime = newRelativeTime
          }
        }
      })
    }
    addListener().catch(console.error)

    return () => {
      if (appStateChangeListener) {
        try {
          appStateChangeListener.remove()
        } catch (error) {
          console.error('YumChallengeTitle | appStateChangeListener.remove() error', error)
        }
      }
    }
  }, [])

  return (
    <div
      className={[classes.wrapper, className].join(' ')}
      style={{
        marginLeft: noMargin ? '0' : isRtl ? '10px' : '20px',
        marginRight: noMargin ? '0' : isRtl ? '20px' : '10px',
      }}
    >
      <h1 className={classes.title}>{title}</h1>
      <div className={classes.time}>{relativeTime}</div>
    </div>
  )
}
